import type * as THREE from 'three/webgpu';

export interface WorldThemeDefinition {
  id: string;
  name: string;
  sky: {
    sunPhiDeg: number;
    sunThetaDeg: number;
    turbidity: number;
    rayleigh: number;
    mieCoefficient: number;
    mieDirectionalG: number;
    fogColor: THREE.ColorRepresentation;
    horizonColor: THREE.ColorRepresentation;
    zenithColor: THREE.ColorRepresentation;
  };
  lighting: {
    exposure: number;
    ambientColor: THREE.ColorRepresentation;
    ambientIntensity: number;
    directionalColor: THREE.ColorRepresentation;
    directionalIntensity: number;
    hemisphereSky: THREE.ColorRepresentation;
    hemisphereGround: THREE.ColorRepresentation;
    hemisphereIntensity: number;
  };
  terrain: {
    baseY: number;
    size: number;
    segments: number;
    innerClearRadius: number;
    landmarkRadius: number;
    outerScatterRadius: number;
    shorelineAngleDeg: number;
    shorelineOffset: number;
    colors: {
      grassDark: THREE.ColorRepresentation;
      grassLight: THREE.ColorRepresentation;
      dune: THREE.ColorRepresentation;
      dirt: THREE.ColorRepresentation;
      rock: THREE.ColorRepresentation;
      rockAccent: THREE.ColorRepresentation;
      crystal: THREE.ColorRepresentation;
      crystalAccent: THREE.ColorRepresentation;
    };
  };
  forceField: {
    color: THREE.ColorRepresentation;
    sparkle: THREE.ColorRepresentation;
  };
  water: {
    level: number;
    size: number;
    offsetX: number;
    offsetZ: number;
    deepColor: THREE.ColorRepresentation;
    shallowColor: THREE.ColorRepresentation;
    causticColor: THREE.ColorRepresentation;
  };
  scatter: {
    landmarkCount: number;
    rockCount: number;
    crystalCount: number;
    debrisCount: number;
    foliageCount: number;
    maxRadius: number;
  };
}

export const DEFAULT_WORLD_THEME: WorldThemeDefinition = {
  id: 'coastal-sci-fi-basin',
  name: 'Coastal Sci-Fi Basin',
  sky: {
    sunPhiDeg: 74,
    sunThetaDeg: 210,
    turbidity: 2.7,
    rayleigh: 4.1,
    mieCoefficient: 0.0025,
    mieDirectionalG: 0.76,
    fogColor: 0x8fc8ee,
    horizonColor: 0xb7ddff,
    zenithColor: 0x4f9ee3,
  },
  lighting: {
    exposure: 1.2,
    ambientColor: 0xfff7ef,
    ambientIntensity: 0.9,
    directionalColor: 0xfff0d8,
    directionalIntensity: 2.45,
    hemisphereSky: 0xc2e3ff,
    hemisphereGround: 0x8db39d,
    hemisphereIntensity: 1.2,
  },
  terrain: {
    baseY: -1,
    size: 2400,
    segments: 168,
    innerClearRadius: 72,
    landmarkRadius: 120,
    outerScatterRadius: 520,
    shorelineAngleDeg: -28,
    shorelineOffset: 95,
    colors: {
      grassDark: 0x669566,
      grassLight: 0x9fd297,
      dune: 0xcfc59f,
      dirt: 0xb78f72,
      rock: 0xa7b1b9,
      rockAccent: 0xd5dde4,
      crystal: 0x74d8f4,
      crystalAccent: 0xd4fbff,
    },
  },
  forceField: {
    color: 0x59b8ff,
    sparkle: 0xc9f5ff,
  },
  water: {
    level: -9,
    size: 1800,
    offsetX: 320,
    offsetZ: -240,
    deepColor: 0x2a74a8,
    shallowColor: 0x65c2d7,
    causticColor: 0xc9fbff,
  },
  scatter: {
    landmarkCount: 16,
    rockCount: 180,
    crystalCount: 96,
    debrisCount: 180,
    foliageCount: 220,
    maxRadius: 520,
  },
};
